import React, { useContext } from "react";
import { DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { HighlightAlt } from "@mui/icons-material";
import bbox from "@turf/bbox";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { Feature, Polygon } from "geojson";
import { defer, uniqBy } from "lodash";
import { LngLatLike } from "mapbox-gl";

import { booleanContains, booleanIntersects } from "fond/turf";
import { isLineString, isPoint } from "fond/types/geojson";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

/**
 * @returns A button that allows the user to select multiple features on the map by drawing a polygon.
 * @description This button uses the `passing_draw_polygon` mode of the `mapbox-gl-draw` library to allow
 * the user to draw a temporary polygon on the map. Once the polygon is drawn, it selects all features that
 * are within or intersecting with the polygon.
 */
const PolygonSelectButton: React.FC<IButtonProps> = () => {
  const { map, drawControl } = useContext(MapContext);

  const handleOnClick = () => {
    drawControl.current?.changeMode("passing_draw_polygon", {
      onDraw: (polygon: Feature<Polygon>) => {
        if (map) {
          // Get the bounding box of the polygon
          // and convert it to a bounding box in pixel coordinates
          // to use with queryRenderedFeatures
          const polygonBounds = bbox(polygon);
          const southWest = [polygonBounds[0], polygonBounds[1]] as LngLatLike;
          const northEast = [polygonBounds[2], polygonBounds[3]] as LngLatLike;
          const northEastPointPixel = map.project(northEast);
          const southWestPointPixel = map.project(southWest);

          // Get all features within the bounding box
          // and filter them to only include those that are within the polygon
          const features = uniqBy(
            map
              ?.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
                layers: [
                  // Select all features within the drawControl styles
                  ...(drawControl.current?.options?.styles?.map(({ id }) => id) || []),
                  // Select all features within the layers specified in the selectableLayerIds
                  ...(drawControl.current?.options?.selectableLayerIds || []),
                ],
              })
              .map((f) => {
                // We need to get the feature from the drawControl, we cannot rely on the geometry within the feature
                // returned from the click function above.
                const drawnFeature = drawControl.current?.get(f.properties?.id) || f;
                return drawnFeature;
              })
              .filter((f) => {
                if (f.id || f.properties?.id !== undefined) {
                  // If editingLayerIds is defined, we only select features from those layerIds
                  if (
                    drawControl.current?.options?.editingLayerIds?.length &&
                    !drawControl.current?.options?.editingLayerIds?.includes(f.properties?.layerId)
                  ) {
                    return false;
                  }
                  // check if feature is within or intersects the polygon
                  if (isPoint(f)) {
                    return booleanPointInPolygon(f, polygon);
                  } else if (isLineString(f)) {
                    return booleanContains(polygon, f) || booleanIntersects(f, polygon);
                  }
                }
                return false;
              }),
            (f) => f.properties?.id || f.id
          );

          defer(() => {
            drawControl.current?.changeMode("simple_select", { featureIds: features.map((f) => f.properties?.id || f.id) });
            map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" } as DrawModeChangeEvent);
            map?.fire("draw.selectionchange", { features });
          });
        }
      },
    });
    map?.fire("draw.modechange", { type: "draw.modechange", mode: "passing_draw_polygon" });
  };

  return (
    <ToolbarToggleButton
      title="Select multiple features"
      value="passing_draw_polygon"
      onClick={handleOnClick}
      data-testid="toolbar-polygon-select-button"
    >
      <HighlightAlt />
    </ToolbarToggleButton>
  );
};

export default PolygonSelectButton;
